export interface CarriedFieldEditing {
  /** Restore an untouched focused answer before validation, collection or autosave. */
  settle(): void;
  dispose(): void;
}

type TextControl = HTMLInputElement | HTMLTextAreaElement;
interface CarriedAnswer {
  value: string;
  placeholder: string | null;
  replacing: boolean;
}

const inputTypes = new Set(['text', 'email', 'tel', 'url', 'number']);

function supported(control: TextControl): boolean {
  // These are presentation-only queries, including the phone country popup.
  // Email's actual input also has role=combobox and must remain eligible.
  return Boolean(control.name) && !(control instanceof HTMLInputElement && !inputTypes.has(control.type))
    && !control.matches('.field-popup-search') && !control.closest('.country-picker, .search-select-popup');
}

function available(control: TextControl): boolean {
  if (control.matches(':disabled') || control.readOnly) return false;
  for (let node: HTMLElement | null = control; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (node.hidden || style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
  }
  return true;
}

/** Opt in only after populating a new office from the previous office's answers. */
export function initializeCarriedFieldEditing(form: HTMLFormElement): CarriedFieldEditing {
  const answers = new Map<TextControl, CarriedAnswer>();
  for (const control of form.querySelectorAll<TextControl>('input, textarea')) {
    // Hidden stages may become visible later; availability is checked on focus.
    if (supported(control) && !control.matches(':disabled') && !control.readOnly && control.value !== '') {
      answers.set(control, { value: control.value, placeholder: control.getAttribute('placeholder'), replacing: false });
    }
  }
  let disposed = false;

  function restorePlaceholder(control: TextControl, answer: CarriedAnswer): void {
    if (answer.placeholder === null) control.removeAttribute('placeholder');
    else control.setAttribute('placeholder', answer.placeholder);
  }

  function finish(control: TextControl, answer: CarriedAnswer): void {
    if (!answer.replacing) return;
    if (control.value === '') control.value = answer.value;
    else answers.delete(control); // A programmatic replacement must also survive.
    answer.replacing = false;
    restorePlaceholder(control, answer);
  }

  function begin(event: Event): void {
    const control = event.target;
    if (!(control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement)) return;
    const answer = answers.get(control);
    if (!answer || answer.replacing || !available(control)) return;
    if (control.value !== answer.value) { answers.delete(control); return; }
    answer.replacing = true;
    control.placeholder = answer.value;
    // Deliberately emit no events: phone widgets retain their canonical original,
    // and autosave must never see a synthetic edit merely from visiting a field.
    control.value = '';
  }

  function edited(event: Event): void {
    const control = event.target as TextControl;
    const answer = answers.get(control);
    if (!answer) return;
    // Canceled composition can end without changing the temporary empty value.
    if (event.type === 'compositionend' && control.value === '') return;
    restorePlaceholder(control, answer);
    answers.delete(control);
  }

  function blur(event: Event): void {
    const control = event.target as TextControl;
    const answer = answers.get(control);
    if (answer) finish(control, answer);
  }

  function settle(): void {
    for (const [control, answer] of answers) finish(control, answer);
  }

  function stepCount(event: KeyboardEvent): void {
    if (event.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey
      || !['ArrowUp', 'ArrowDown'].includes(event.key)) return;
    const control = event.target;
    if (!(control instanceof HTMLInputElement) || !control.closest('.count-field')) return;
    const answer = answers.get(control);
    // Count's own key handler must step the carried quantity, not the temporary
    // empty display. Its resulting input/change events then commit the edit.
    if (answer) finish(control, answer);
  }

  // Capture precedes existing widget focus/input listeners. A click also resumes
  // replacement if a draft flush settled the answer while it retained focus.
  form.addEventListener('focus', begin, true);
  form.addEventListener('click', begin, true);
  form.addEventListener('beforeinput', begin, true);
  form.addEventListener('blur', blur, true);
  form.addEventListener('input', edited, true);
  form.addEventListener('change', edited, true);
  form.addEventListener('compositionend', edited, true);
  form.addEventListener('keydown', stepCount, true);
  return {
    settle,
    dispose(): void {
      if (disposed) return;
      disposed = true;
      settle();
      answers.clear();
      form.removeEventListener('focus', begin, true);
      form.removeEventListener('click', begin, true);
      form.removeEventListener('beforeinput', begin, true);
      form.removeEventListener('blur', blur, true);
      form.removeEventListener('input', edited, true);
      form.removeEventListener('change', edited, true);
      form.removeEventListener('compositionend', edited, true);
      form.removeEventListener('keydown', stepCount, true);
    },
  };
}
